import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import SafeProductImage from 'components/kea/SafeProductImage';
import IntlMessages from 'util/IntlMessages';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const S = {
  wrapper: {
    border: 'none',
    borderRadius: 0,
    overflow: 'visible',
    marginBottom: 0,
    background: '#fff',
  },
  searchRow: {
    display: 'flex',
    gap: 8,
    padding: '16px 20px 10px',
    background: '#fbfefd',
  },
  searchInput: {
    flex: 1,
    minHeight: 40,
    padding: '8px 12px',
    border: '1px solid #d5e1dd',
    borderRadius: 8,
    background: '#fff',
    color: '#1e2248',
    fontSize: 13,
    outline: 'none',
  },
  searchHint: {
    fontSize: 11,
    lineHeight: 1.45,
    color: '#66708a',
    padding: '0 20px 12px',
    borderBottom: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  dropdownPortal: {
    position: 'fixed',
    background: '#fff',
    border: '1px solid #ddeae5',
    borderRadius: 8,
    boxShadow: '0 12px 28px rgba(30, 34, 72, 0.14)',
    zIndex: 9999,
    maxHeight: 320,
    overflowY: 'auto',
  },
  dropdownItem: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr',
    gap: 10,
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid #edf2f0',
    cursor: 'pointer',
    userSelect: 'none',
  },
  dropdownItemHovered: {
    background: '#f3fbf8',
  },
  dropdownThumb: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: 'cover',
    background: '#f1f3f5',
    flexShrink: 0,
  },
  dropdownThumbEmpty: {
    width: 40,
    height: 40,
    borderRadius: 6,
    background: '#f1f3f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8790a5',
    fontSize: 18,
    flexShrink: 0,
  },
  dropdownInfo: { minWidth: 0 },
  dropdownName: { fontWeight: 600, fontSize: 13, color: '#1e2248', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  dropdownMeta: { fontSize: 11, color: '#66708a', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' },
  dropdownId: { fontSize: 10, color: '#8790a5', fontWeight: 400 },
  dropdownUnavail: { fontSize: 10, color: '#a9455e', fontWeight: 600 },
  dropdownPrice: { fontSize: 12, color: '#1e2248', whiteSpace: 'nowrap', flexShrink: 0 },

  dropdownEmpty: {
    padding: '18px 14px',
    fontSize: 13,
    color: '#66708a',
    textAlign: 'center',
  },
  zone: { background: '#fff', padding: '12px 16px' },
  emptyState: {
    padding: '20px 16px',
    color: '#66708a',
    fontSize: 13,
    textAlign: 'center',
    border: '1px dashed #c8d9d2',
    borderRadius: 6,
    background: '#fbfefd',
  },
  pickedRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto',
    gap: 10,
    alignItems: 'center',
    minHeight: 56,
    padding: '8px 4px',
    borderBottom: '1px solid #edf2f0',
    background: '#fff',
  },
  pickedThumb: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: 'cover',
    background: '#f1f3f5',
    flexShrink: 0,
  },
  pickedThumbEmpty: {
    width: 40,
    height: 40,
    borderRadius: 6,
    background: '#f1f3f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8790a5',
    fontSize: 14,
    flexShrink: 0,
  },
  pickedInfo: { minWidth: 0 },
  pickedName: { fontWeight: 600, fontSize: 13, color: '#1e2248', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pickedMeta: { fontSize: 11, color: '#66708a', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' },
  pickedId: { fontSize: 10, color: '#8790a5', fontWeight: 400 },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8790a5',
    fontSize: 16,
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 6,
    lineHeight: 1,
    flexShrink: 0,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(price) {
  if (price == null) return '';
  return Number(price).toFixed(2);
}

function ProductThumb({ url, size = S.dropdownThumb, emptySize = S.dropdownThumbEmpty }) {
  return <SafeProductImage src={url} alt="" style={url ? size : emptySize} />;
}

// ---------------------------------------------------------------------------
// ProductSelector
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 300;
const NUMERIC_RE = /^#?\d+$/;
const MIN_QUERY_LENGTH = 2;
let productSelectorInstanceId = 0;

/**
 * Reusable admin Product Selector widget.
 *
 * Controlled component — picked product state lives in the parent.
 *
 * @param {object}   props
 * @param {object}   props.api            productSelectorApi (from serviceRegistry)
 * @param {Array}    props.pickedProducts  Array of canonical product cards currently selected
 * @param {Function} props.onPick         Called with a product card when user selects from dropdown
 * @param {Function} props.onRemove       Called with productId (string) when user removes a selection
 * @param {string}   [props.placeholder]  Search box placeholder text
 * @param {number}   [props.limit=8]      Max dropdown results
 */
const ProductSelector = ({
  api,
  pickedProducts = [],
  onPick,
  onRemove,
  placeholder,
  limit = 8,
  showPickedZone = true,
}) => {
  const t = useLocalizedMessage();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [searchError, setSearchError] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  const [dropdownId] = useState(() => {
    productSelectorInstanceId += 1;
    return `product-selector-results-${productSelectorInstanceId}`;
  });
  const [displayPickedProducts, setDisplayPickedProducts] = useState(() =>
    pickedProducts.map((product) => ({ ...product, id: String(product.id) })));

  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const searchRowRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInFlightRef = useRef(false);
  const queuedSearchRef = useRef(null);
  const desiredQueryRef = useRef('');
  const mountedRef = useRef(true);

  const pickedIds = new Set(pickedProducts.map((p) => String(p.id)));
  const pickedFingerprint = pickedProducts.map((product) => String(product.id)).join('\u001f');

  useEffect(() => {
    let active = true;
    const normalized = pickedProducts.map((product) => ({
      ...product,
      id: String(product.id),
    }));
    setDisplayPickedProducts(normalized);
    setResolveError(null);
    const unresolvedIds = normalized
      .filter((product) => !product.name || product.unresolved)
      .map((product) => product.id);
    if (unresolvedIds.length === 0 || typeof api?.resolveProducts !== 'function') {
      return () => { active = false; };
    }
    api.resolveProducts(unresolvedIds).then((response) => {
      if (!active) return;
      const resolvedById = Object.fromEntries(
        (response?.items || []).map((product) => [String(product.id), {
          ...product,
          id: String(product.id),
          unresolved: false,
        }]),
      );
      setDisplayPickedProducts(normalized.map((product) =>
        resolvedById[product.id] || {
          ...product,
          unresolved: true,
        }));
    }).catch((error) => {
      if (!active) return;
      setResolveError(error?.message || 'product.selector.resolve.error');
    });
    return () => { active = false; };
    // The fingerprint intentionally isolates catalog resolution from cosmetic
    // object identity changes in controlled parents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, pickedFingerprint]);

  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(debounceRef.current);
  }, []);

  // Close dropdown when clicking outside both the widget and the portal dropdown
  useEffect(() => {
    const handler = (e) => {
      const inWrapper  = wrapperRef.current  && wrapperRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrapper && !inDropdown) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = useCallback(
    async function searchLatest(q) {
      if (!api) return;
      desiredQueryRef.current = q;
      if (searchInFlightRef.current) {
        queuedSearchRef.current = q;
        return;
      }
      searchInFlightRef.current = true;
      queuedSearchRef.current = null;
      setSearching(true);
      setSearchError(null);
      try {
        const items = await api.searchProducts(q, limit);
        if (mountedRef.current && desiredQueryRef.current === q) {
          setResults((items || []).map((item) => ({ ...item, id: String(item.id) })));
          setHoveredIndex(-1);
          setDropdownOpen(true);
        }
      } catch (error) {
        if (mountedRef.current && desiredQueryRef.current === q) {
          setResults([]);
          setHoveredIndex(-1);
          setDropdownOpen(true);
          setSearchError(error?.message || t('product.selector.search.error'));
        }
      } finally {
        searchInFlightRef.current = false;
        const queued = queuedSearchRef.current;
        queuedSearchRef.current = null;
        if (queued && queued !== q) searchLatest(queued);
        else if (mountedRef.current) setSearching(false);
      }
    },
    [api, limit, t],
  );

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setQuery(value);

      clearTimeout(debounceRef.current);

      const trimmed = value.trim();
      desiredQueryRef.current = trimmed;
      const isNumeric = NUMERIC_RE.test(trimmed);
      if (trimmed === '' || (!isNumeric && trimmed.length < MIN_QUERY_LENGTH)) {
        queuedSearchRef.current = null;
        setResults([]);
        setHoveredIndex(-1);
        setDropdownOpen(false);
        return;
      }

      // Numeric queries skip the debounce for snappier feel
      const delay = isNumeric ? 0 : DEBOUNCE_MS;
      debounceRef.current = setTimeout(() => {
        runSearch(trimmed);
      }, delay);
    },
    [runSearch],
  );

  const handlePick = useCallback(
    (product) => {
      const productId = String(product.id);
      if (pickedIds.has(productId)) return;
      if (onPick) onPick({ ...product, id: productId });
      setQuery('');
      setResults([]);
      setHoveredIndex(-1);
      setDropdownOpen(false);
    },
    [onPick, pickedIds],
  );

  const handleRemove = useCallback(
    (productId) => {
      if (onRemove) onRemove(String(productId));
    },
    [onRemove],
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape' && dropdownOpen) {
        event.preventDefault();
        setDropdownOpen(false);
        setHoveredIndex(-1);
        return;
      }
      if (!dropdownOpen || results.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHoveredIndex((current) => (current + 1) % results.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHoveredIndex((current) =>
          current <= 0 ? results.length - 1 : current - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const product = results[hoveredIndex >= 0 ? hoveredIndex : 0];
        if (product) handlePick(product);
      }
    },
    [dropdownOpen, handlePick, hoveredIndex, results],
  );

  const searchPlaceholder = placeholder || t('product.selector.search.placeholder');

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 300 });

  const updateDropdownPos = useCallback(() => {
    if (!searchRowRef.current) return;
    const rect = searchRowRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  // Keep portal dropdown anchored to the search row on scroll / resize
  useEffect(() => {
    if (!dropdownOpen) return;
    updateDropdownPos();
    window.addEventListener('scroll', updateDropdownPos, true);
    window.addEventListener('resize', updateDropdownPos);
    return () => {
      window.removeEventListener('scroll', updateDropdownPos, true);
      window.removeEventListener('resize', updateDropdownPos);
    };
  }, [dropdownOpen, updateDropdownPos]);

  return (
    <>
      <div style={S.wrapper} ref={wrapperRef}>
        {/* Search row */}
        <div style={S.searchRow} ref={searchRowRef}>
          <input
            type="text"
            style={S.searchInput}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={dropdownOpen}
            aria-controls={dropdownOpen ? dropdownId : undefined}
            aria-activedescendant={
              dropdownOpen && hoveredIndex >= 0
                ? `${dropdownId}-option-${hoveredIndex}`
                : undefined
            }
            autoComplete="off"
          />
          {searching && (
            <span style={{ alignSelf: 'center', fontSize: 12, color: '#66708a' }}>
              {t('product.selector.searching')}
            </span>
          )}
        </div>

        <div style={S.searchHint}>
          {t('product.selector.search.hint')}
        </div>
        {searchError && (
          <div role="alert" style={{ padding: '8px 14px', color: '#b00020', fontSize: 12 }}>
            {searchError}
          </div>
        )}
        {resolveError && (
          <div role="alert" style={{ padding: '8px 14px', color: '#b00020', fontSize: 12 }}>
            {resolveError === 'product.selector.resolve.error'
              ? t(resolveError)
              : resolveError}
          </div>
        )}

        {showPickedZone && (
          <div style={S.zone}>
            {displayPickedProducts.length === 0 ? (
              <div style={S.emptyState}>
                {t('product.selector.empty')}
              </div>
            ) : (
              displayPickedProducts.map((product) => (
                <div key={product.id} style={S.pickedRow}>
                  <ProductThumb
                    url={product.thumbnail}
                    size={S.pickedThumb}
                    emptySize={S.pickedThumbEmpty}
                  />
                  <div style={S.pickedInfo}>
                    <div style={S.pickedName}>
                      {product.name || t('product.selector.unresolved')}
                      <span style={S.pickedId}> #{product.id}</span>
                    </div>
                    <div style={S.pickedMeta}>
                      {product.vendor && <span>{product.vendor}</span>}
                      {product.vendorCode && <span style={{ color: '#8790a5' }}>{product.vendorCode}</span>}
                      {product.price > 0 && <span>{formatPrice(product.price)}</span>}
                      {!product.available && (
                        <span style={{ color: '#a9455e', fontWeight: 600, fontSize: 10 }}>
                          {t('product.selector.unavailable')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={S.removeBtn}
                    onClick={() => handleRemove(product.id)}
                    aria-label={t('product.selector.remove')}
                  >
                    <IntlMessages id="ui.productSelector.productSelector.copy" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Portal dropdown — rendered into document.body to escape container clipping */}
      {dropdownOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          id={dropdownId}
          data-testid="ps-dropdown"
          role="listbox"
          aria-label={searchPlaceholder}
          style={{ ...S.dropdownPortal, ...dropdownPos }}
        >
          {results.length === 0 ? (
            <div style={S.dropdownEmpty}>
              {t('product.selector.no_results')}
            </div>
          ) : (
            results.map((product, idx) => {
              const alreadyPicked = pickedIds.has(String(product.id));
              return (
                <div
                  key={product.id}
                  id={`${dropdownId}-option-${idx}`}
                  role="option"
                  aria-label={product.name}
                  aria-selected={alreadyPicked}
                  style={{
                    ...S.dropdownItem,
                    ...(hoveredIndex === idx ? S.dropdownItemHovered : {}),
                    opacity: alreadyPicked ? 0.45 : 1,
                    cursor: alreadyPicked ? 'default' : 'pointer',
                  }}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  onClick={() => handlePick(product)}
                >
                  <ProductThumb
                    url={product.thumbnail}
                    size={S.dropdownThumb}
                    emptySize={S.dropdownThumbEmpty}
                  />
                  <div style={S.dropdownInfo}>
                    <div style={S.dropdownName}>
                      {product.name}
                      <span style={S.dropdownId}> #{product.id}</span>
                    </div>
                    <div style={S.dropdownMeta}>
                      {product.vendor && <span>{product.vendor}</span>}
                      {product.vendorCode && <span style={{ color: '#8790a5' }}>{product.vendorCode}</span>}
                      {product.price > 0 && <span style={S.dropdownPrice}>{formatPrice(product.price)}</span>}
                      {!product.available && (
                        <span style={S.dropdownUnavail}>
                          {t('product.selector.unavailable')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ProductSelector;
